import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { fmtNum } from "@/lib/format";

const fmtMonth = (k) => new Date(`${k}-01`).toLocaleString("en-IN", { month: "short", year: "2-digit" });

function Chart({ data, height = 220 }) {
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 600, height: 240 }}>
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} width={44} />
          <Tooltip formatter={(v, n) => (n === "DP trend" ? [null, null] : [`₹ ${fmtNum(Math.round(v))}`, n])} labelFormatter={fmtMonth} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="da" name="DA · supplier FOC" stackId="a" fill="#f59e0b" radius={[0, 0, 0, 0]} />
          <Bar dataKey="dp" name="DP · process loss" stackId="a" fill="#e11d48" radius={[3, 3, 0, 0]} />
          <Line type="monotone" dataKey="dp" name="DP trend" stroke="#881337" strokeWidth={2} dot={{ r: 3 }} legendType="none" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function LossTrend() {
  const [months, setMonths] = useState(6);
  const q = useQuery({ queryKey: ["loss-trend", months], queryFn: async () => (await api.get("/production-loss/trend", { params: { months } })).data, staleTime: 60_000 });
  const d = q.data;
  const last = d?.overall?.[d.overall.length - 1];
  const prev = d?.overall?.[d.overall.length - 2];
  const delta = last && prev && prev.dp ? Math.round(((last.dp - prev.dp) / prev.dp) * 100) : null;
  return (
    <div className="pp-card p-5 space-y-4 min-w-0 overflow-x-auto" data-testid="loss-trend-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><div className="pp-label">Monthly DA vs DP trend</div><div className="text-sm text-slate-500">₹ per month · stacked DA (amber) + DP (rose) · DP line shows whether process losses are falling{delta != null && <span className={`ml-2 font-semibold ${delta <= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="loss-trend-delta">{delta <= 0 ? "▼" : "▲"} {Math.abs(delta)}% DP vs last month</span>}</div></div>
        <select className="pp-select h-8 w-32" value={months} onChange={(e) => setMonths(Number(e.target.value))} data-testid="loss-trend-months"><option value={3}>3 months</option><option value={6}>6 months</option><option value={12}>12 months</option></select>
      </div>
      {d && <Chart data={d.overall} height={240} />}
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {d && Object.entries(d.sections).map(([sec, rows]) => (
          <div key={sec} className="rounded-md border border-slate-200 p-2" data-testid={`loss-trend-${sec}`}>
            <div className="flex justify-between text-xs mb-1"><span className="font-semibold">{sec}</span><span className="font-mono text-slate-500">₹ {fmtNum(Math.round(rows.reduce((a, r) => a + r.da + r.dp, 0)))}</span></div>
            <Chart data={rows} height={130} />
          </div>
        ))}
      </div>
    </div>
  );
}
